/**
 * A tela de comentários no site ao vivo (ORD-F-026, PR 6): a troca e a ligação, no JS crítico; a tela mesma
 * (commentsScreenLive.tsx) num pedaço à parte. Só existe com `features.commentary_timeline` E as mesmas condições do
 * painel de comentários (`features.commentary`, a camada no snapshot, depois da abertura, fora da TV). Desligada (o
 * padrão), nada aparece e nada é pedido: um endereço com `tela=comentarios` mostra a Apuração.
 *
 * A ligação junta os dois pedaços sob demanda: o índice vem do pedaço do painel (o mesmo pedido a cada seq) e vai à tela
 * por prop; o Esc é registrado aqui. Se um pedaço importasse o outro, ou a tela importasse a cascata do Esc, o Rolldown
 * tiraria o núcleo do `index` num pedaço comum (DE-C20; medido no PR 6).
 */
import { ESC_PRIORITY, useEscapeLayer } from '../a11y/escape';
import { ScreenToggle } from '../features/commentary/ScreenToggle';
import { navigate, setScreen } from '../state/actions';
import { liveScreenAvailable, liveScreenOn } from '../state/derived';
import { commentaryChunk } from './commentarySlot';
import { lazyChunk, useLazyChunk } from './lazyChunk';
import { placeRouteOf } from './screenNav';

export const screenChunk = lazyChunk(() => import('./commentsScreenLive'));

/** A tela existe agora no ao vivo (derivado: avisa só quando muda). */
export function useLiveScreenAvailable(): boolean {
  return liveScreenAvailable.value;
}

/** A tela está aberta (derivado: a casca do App não se redesenha a cada troca de lugar). */
export function useLiveScreenOn(): boolean {
  return liveScreenOn.value;
}

export function LiveScreenToggle({ variant }: { variant: 'header' | 'bar' }) {
  return useLiveScreenAvailable() ? <ScreenToggle variant={variant} /> : null;
}

type PanelModule = typeof import('./commentaryLive');
type ScreenModule = typeof import('./commentsScreenLive');

/** Monta só com os dois pedaços carregados: o hook do índice roda sempre na mesma ordem. */
function Screen({ panel, screen }: { panel: PanelModule; screen: ScreenModule }) {
  const index = panel.useLiveCommentary();
  const { LiveCommentsScreen } = screen;
  return (
    <LiveCommentsScreen
      index={index}
      onMap={(thread) => navigate({ screen: undefined, place: placeRouteOf(thread) })}
      loadHours={panel.loadNightHours}
    />
  );
}

export function LiveScreenSlot() {
  const panel = useLazyChunk(commentaryChunk, true);
  const screen = useLazyChunk(screenChunk, true);
  useEscapeLayer(ESC_PRIORITY.screen, true, () => setScreen(null));
  if (!panel || !screen)
    return <div class="comments-screen-wait" aria-busy="true" data-testid="comments-screen-wait" />;
  return <Screen panel={panel} screen={screen} />;
}
